import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {ApiUrl} from '../shared/models/apiurl.models';
import {withoutGlobalSpinner} from '../theme/utils/spinner-context';
import {
  CouponBenefits,
  CouponErrorBody,
  CouponRedeemResponse,
  EligibleCouponRedemption,
  EligibleCouponsResponse,
} from '../models/coupon';

/**
 * SCRUM-311 / 312 / 313 / 328 - coupon ad entitlement, client side.
 *
 * The server is the only gate (ownership, validity, remaining units,
 * idempotency). This service just calls the three client endpoints and keeps
 * the last "ההטבות שלי" payload so the activation form and the benefits list
 * stay in sync after a redemption (the redeem response carries `benefits`).
 * Auth: the app interceptor attaches the in-memory JWT.
 */
@Injectable({providedIn: 'root'})
export class CouponService {

  /** Last GET my/ (or redeem `benefits`) payload; null until loaded. */
  readonly benefits$ = new BehaviorSubject<CouponBenefits | null>(null);

  /**
   * A coupon the user explicitly picked in the Adaline plan cards. The plan
   * picker at the end of the publish form pre-highlights it (the user still
   * has to press "פרסם באמצעות הקופון"); read once with takePreferred().
   */
  private preferredRedemptionId: number | null = null;

  constructor(private http: HttpClient, private apiUrl: ApiUrl) {}

  /** POST redeem/ - full response so the caller can check for 201. */
  redeem(code: string): Observable<HttpResponse<CouponRedeemResponse>> {
    return this.http.post<CouponRedeemResponse>(
      this.apiUrl.getCouponRedeemUrl(),
      {code},
      {observe: 'response', context: withoutGlobalSpinner()},
    ).pipe(
      tap(res => {
        if (res.status === 201 && res.body?.benefits) this.benefits$.next(res.body.benefits);
      }),
    );
  }

  /** GET my/ - "ההטבות שלי" (summary, coupons, eligible, features). */
  loadMyBenefits(): Observable<CouponBenefits> {
    return this.http.get<CouponBenefits>(this.apiUrl.getMyCouponsUrl(), {context: withoutGlobalSpinner()})
      .pipe(tap(b => this.benefits$.next(b)));
  }

  /** GET eligible/ - usable coupons for the plan picker, in server order. */
  loadEligible(): Observable<EligibleCouponRedemption[]> {
    return this.http.get<EligibleCouponsResponse>(this.apiUrl.getEligibleCouponsUrl(), {context: withoutGlobalSpinner()})
      .pipe(map(body => Array.isArray(body?.results) ? body.results : []));
  }

  setPreferred(redemptionId: number | null): void {
    this.preferredRedemptionId = redemptionId;
  }

  takePreferred(): number | null {
    const id = this.preferredRedemptionId;
    this.preferredRedemptionId = null;
    return id;
  }

  /** A unique id for ONE publish attempt (idempotency key). */
  newRequestId(): string {
    const c: any = (typeof globalThis !== 'undefined') ? (globalThis as any).crypto : undefined;
    try {
      if (c?.randomUUID) return c.randomUUID();
      if (c?.getRandomValues) {
        const b = new Uint8Array(16);
        c.getRandomValues(b);
        b[6] = (b[6] & 0x0f) | 0x40;
        b[8] = (b[8] & 0x3f) | 0x80;
        const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
        return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
      }
    } catch (e) {
      // fall through
    }
    return 'web-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12)
      + Math.random().toString(36).slice(2, 12);
  }

  /** The `{error, message}` body of a refusal, if there is one. */
  errorBody(err: unknown): CouponErrorBody | null {
    const body = (err as HttpErrorResponse)?.error;
    return body && typeof body === 'object' ? body as CouponErrorBody : null;
  }

  /** True when the publish refusal is about the coupon (reload the eligible list). */
  isCouponRefusal(err: unknown): boolean {
    const body = this.errorBody(err);
    if (!body) return false;
    const code = String(body.error || '');
    return body.refresh_coupons === true || code.startsWith('COUPON_') || code.startsWith('CAMPAIGN_');
  }

  /** Server message, else a generic Hebrew fallback. */
  errorMessage(err: unknown, fallback: string): string {
    const body = this.errorBody(err);
    return (body?.message && String(body.message)) || fallback;
  }
}
